<template>
  <PageChrome>
    <template #title>
      <span class="eyebrow">Reports</span>
    </template>

    <!-- ── Tab navigation ──────────────────────────────────────── -->
    <nav class="tabNav">
      <button :class="['tabBtn', activeTab === 'team-activity' && 'active']" @click="navigateToTab('team-activity')">
        Team activity
      </button>
      <button :class="['tabBtn', activeTab === 'deliverability' && 'active']" @click="navigateToTab('deliverability')">
        Deliverability
      </button>
    </nav>

    <!-- ── Tab content ─────────────────────────────────────────── -->
    <TeamActivityTab v-if="activeTab === 'team-activity'" />
    <DeliverabilityTab v-else-if="activeTab === 'deliverability'" />
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import PageChrome from '../components/PageChrome.vue'
import TeamActivityTab from '../components/reports/TeamActivityTab.vue'
import DeliverabilityTab from '../components/reports/DeliverabilityTab.vue'
import { useReportsFilters, REPORTS_FILTER_KEY } from '../composables/useReportsFilters'

// ── Shared filters (persists across tab switches + localStorage) ────
provide(REPORTS_FILTER_KEY, useReportsFilters())

// ── Routing / tabs ──────────────────────────────────────────────────
const route = useRoute()
const router = useRouter()

const VALID_TABS = ['team-activity', 'deliverability'] as const
type Tab = (typeof VALID_TABS)[number]

const activeTab = computed<Tab>(() => {
  const tab = String(route.params.tab || '')
  return (VALID_TABS as readonly string[]).includes(tab) ? (tab as Tab) : 'team-activity'
})

function navigateToTab(tab: Tab) {
  router.replace({ name: 'reports', params: { tab } })
}
</script>

<style scoped>
/* ── Tabs (same as CandidatesPage) ──────────────────────────────── */
.tabNav {
  display: flex;
  gap: var(--space-1);
  border-bottom: 1px solid var(--color-border-subtle);
  margin-bottom: var(--space-5);
}
.tabBtn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border: none;
  background: none;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--color-text-tertiary);
  cursor: pointer;
  transition: color var(--duration-fast) var(--ease-out);
  white-space: nowrap;
}
.tabBtn:hover { color: var(--color-text-primary); }
.tabBtn.active {
  color: var(--color-text-primary);
  font-weight: var(--fw-semibold);
}
.tabBtn.active::after {
  content: '';
  position: absolute;
  left: var(--space-3);
  right: var(--space-3);
  bottom: -1px;
  height: 2px;
  background: var(--color-accent);
  border-radius: 1px;
}
</style>
